import { forwardRef } from 'react';
import Icon from './Icon.jsx';
import Spinner from './Spinner.jsx';

/**
 * The one button of the app. Variants: primary (one per view), secondary, ghost, danger.
 * While `loading`, it keeps its label and width, shows progress and ignores clicks.
 * Icon-only buttons must pass `label`, which becomes their accessible name.
 * Forwards its ref so tooltips and menus can anchor to it.
 */
const Button = forwardRef(({ variant = 'secondary', size = 'md', loading = false, disabled = false, iconStart, iconEnd, iconOnly = false, label, type = 'button', children, ...rest }, ref) => {
  if (iconOnly && !label) {
    throw new Error('Button: icon-only buttons need a label');
  }
  const iconSize = size === 'lg' ? 'lg' : 'md';
  const classes = ['comp-button', `comp-button--${variant}`, `comp-button--${size}`];
  if (iconOnly) {
    classes.push('comp-button--icon-only');
  }
  if (loading) {
    classes.push('is-loading');
  }

  return (
    <button {...rest} ref={ref} type={type} className={classes.join(' ')} disabled={disabled || loading} aria-busy={loading || undefined} aria-label={iconOnly ? label : undefined}>
      {loading && <Spinner size={iconSize === 'lg' ? 18 : 16} />}
      {!loading && iconStart && <Icon icon={iconStart} size={iconSize} />}
      {!iconOnly && <span className="comp-button__label">{children}</span>}
      {!iconOnly && iconEnd && <Icon icon={iconEnd} size={iconSize} />}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
